import { motion } from 'framer-motion';
import type { CSSProperties, FunctionComponent, MouseEvent, ReactNode } from 'react';
import { memo, useEffect, useState } from 'react';

import type { ChatContext } from './chat-context-types';
import {
    useChatComposerActive,
    useChatComposerCallbacks,
    useChatComposerConversation,
    useChatComposerInput,
} from './components/ChatComposerContext';
import { ChatIdleSlotOutlet } from './components/ChatIdleSlotOutlet';
import { ChatPromptOutlet } from './components/ChatPromptOutlet';
import { PILL_COMPOSER_WIDTH_PX, PillChatComposer } from './components/PillChatComposer';
import { CHAT_BAR_HEIGHT_PX, COMPOSER_LAYOUT_ID, COMPOSER_TRANSITION } from './constants';
import { useRegisterChatContext } from './hooks/use-register-chat-context';

// Constant footprint so inter-tile morph is pure position change —
// the composer grows upward via absolute bottom-anchoring inside.
const PILL_LAYOUT_BOX_HEIGHT_PX = CHAT_BAR_HEIGHT_PX - 2;

// Recede non-chat tiles 1% while the sticky chat overlaps them. Applied
// per-tile (not on a shared row) since TilingEngine renders siblings.
const TILE_RECEDED_SCALE = 0.99;
const TILE_SCALE_TRANSITION = 'transform 200ms ease-out';

// One-shot slide-in for the initial composer appearance.
const PILL_ENTRY_OFFSET_Y_PX = CHAT_BAR_HEIGHT_PX + 16;
const PILL_ENTRY_INITIAL = { opacity: 0, y: PILL_ENTRY_OFFSET_Y_PX } as const;
const PILL_ENTRY_ANIMATE = { opacity: 1, y: 0 } as const;

const FOCUS_GRADIENT_HEIGHT_PX = 150;
const FOCUS_GRADIENT_COLOR = 'var(--color-gray-100)';
const FOCUS_GRADIENT_BACKGROUND = `linear-gradient(to top, ${FOCUS_GRADIENT_COLOR} 0%, ${FOCUS_GRADIENT_COLOR} 35%, color-mix(in srgb, ${FOCUS_GRADIENT_COLOR} 93%, transparent) 65%, transparent 100%)`;
const FOCUS_GRADIENT_MASK = 'linear-gradient(to top, black 0%, black 50%, transparent 90%)';
const FOCUS_GRADIENT_STYLE: CSSProperties = {
    height: FOCUS_GRADIENT_HEIGHT_PX,
    background: FOCUS_GRADIENT_BACKGROUND,
    backdropFilter: 'blur(14px)',
    WebkitBackdropFilter: 'blur(14px)',
    maskImage: FOCUS_GRADIENT_MASK,
    WebkitMaskImage: FOCUS_GRADIENT_MASK,
};
const FOCUS_GRADIENT_TRANSITION = { duration: 0.32, ease: [0.25, 1, 0.5, 1] as const };

// Composer height threshold above which the focus gradient is revealed —
// keeps the gradient hidden for the bare collapsed pill, shows it once the
// topSlot expands.
const COMPOSER_GRADIENT_REVEAL_HEIGHT_PX = 50;

type TileWithChatProps = {
    readonly tileId: string;
    readonly children: ReactNode;
    readonly chatContext?: ChatContext;
};

// Uses `onMouseOver` + stopPropagation (not `onMouseEnter`): sidepanel tiles
// portal through createPortal but React treats them as main-tile descendants —
// mouseenter never re-fires when crossing from sidepanel back to main.
//
// Composer rendering is gated by `isComposerEnabled` from the provider, so any
// tile (main or sidepanel) renders neither the spacer nor the pill until the
// root opts in (typically via the `ai-chat` feature flag).
export const TileWithChat: FunctionComponent<TileWithChatProps> = ({ chatContext, children, tileId }) => {
    const { isComposerEnabled, setHoveredTile } = useChatComposerCallbacks();
    const { isContentUnderChat } = useChatComposerActive();

    useRegisterChatContext(tileId, chatContext);

    return (
        // biome-ignore lint/a11y/noStaticElementInteractions: hover/focus only updates which tile the floating composer pill anchors under; non-pointer users always see the default anchor.
        <div
            className="relative flex h-full min-h-0 flex-col"
            onFocus={() => setHoveredTile(tileId)}
            onMouseOver={(event: MouseEvent<HTMLDivElement>) => {
                event.stopPropagation();
                setHoveredTile(tileId);
            }}
            style={{
                transform: isContentUnderChat ? `scale(${TILE_RECEDED_SCALE})` : 'scale(1)',
                transformOrigin: 'center center',
                transition: TILE_SCALE_TRANSITION,
            }}
        >
            <div className="flex min-h-0 flex-1 flex-col">{children}</div>
            {isComposerEnabled && <TileChatRail tileId={tileId} />}
        </div>
    );
};

const TileChatRail: FunctionComponent<{ readonly tileId: string }> = memo(({ tileId }) => {
    const { activeTileId, isExpanded } = useChatComposerActive();
    const { hasComposerAppeared, isComposerReady, markComposerAppeared } = useChatComposerCallbacks();

    const showCollapsedComposer = isComposerReady && !isExpanded && activeTileId === tileId;

    return (
        <>
            <motion.div
                className="shrink-0"
                animate={{ height: isExpanded || !isComposerReady ? 0 : CHAT_BAR_HEIGHT_PX }}
                transition={COMPOSER_TRANSITION}
                initial={false}
                aria-hidden={true}
            />
            <ChatComposerSlot
                show={showCollapsedComposer}
                hasAppeared={hasComposerAppeared}
                onAppeared={markComposerAppeared}
            />
        </>
    );
});

const ChatComposerSlot: FunctionComponent<{
    readonly show: boolean;
    readonly hasAppeared: boolean;
    readonly onAppeared: () => void;
}> = memo(({ show, hasAppeared, onAppeared }) => {
    const { setCollapsedComposerOpen } = useChatComposerCallbacks();
    const [isComposerOpen, setIsComposerOpen] = useState(false);
    // Callback ref + ResizeObserver: usehooks-ts useResizeObserver attaches
    // its observe effect once on mount, so a ref whose node only mounts later
    // (gated by `show`) never gets observed. The callback ref re-fires when
    // the node attaches, kicking off observation at the right moment.
    const [composerNode, setComposerNode] = useState<HTMLDivElement | null>(null);
    const [isComposerTall, setIsComposerTall] = useState(false);

    useEffect(() => {
        if (composerNode == null) {
            return;
        }
        // Track only the threshold-crossing to avoid per-frame re-renders while
        // the inner composer height animates.
        const observer = new ResizeObserver(([entry]) => {
            if (entry == null) return;
            const tall = entry.contentRect.height > COMPOSER_GRADIENT_REVEAL_HEIGHT_PX;
            setIsComposerTall((previous) => (previous === tall ? previous : tall));
        });
        observer.observe(composerNode);
        return () => observer.disconnect();
    }, [composerNode]);

    useEffect(() => {
        if (!show) setIsComposerOpen(false);
    }, [show]);

    useEffect(() => {
        setCollapsedComposerOpen(show && isComposerOpen);
    }, [isComposerOpen, setCollapsedComposerOpen, show]);

    const overlayShown = show && isComposerOpen && isComposerTall;

    return (
        <div className="pointer-events-none absolute inset-x-0 bottom-0 z-50 overflow-visible">
            <motion.div
                aria-hidden={true}
                className="absolute inset-x-0 bottom-0"
                initial={false}
                animate={{ opacity: overlayShown ? 1 : 0 }}
                transition={FOCUS_GRADIENT_TRANSITION}
                style={{ ...FOCUS_GRADIENT_STYLE, pointerEvents: 'none' }}
            />
            {/* Bottom-anchored so topSlot growth expands upward. */}
            <div
                className="absolute inset-x-0 flex items-end justify-center pb-[2px]"
                style={{ minHeight: CHAT_BAR_HEIGHT_PX, bottom: 0 }}
            >
                {show && (
                    <motion.div
                        layoutId={COMPOSER_LAYOUT_ID}
                        // Position-only: size is owned by PillChatComposer's own
                        // animations. layout="size" would conflict with the topSlot tween.
                        layout="position"
                        className="pointer-events-auto relative"
                        style={{ width: PILL_COMPOSER_WIDTH_PX, height: PILL_LAYOUT_BOX_HEIGHT_PX }}
                        initial={hasAppeared ? false : PILL_ENTRY_INITIAL}
                        animate={hasAppeared ? undefined : PILL_ENTRY_ANIMATE}
                        transition={COMPOSER_TRANSITION}
                        onAnimationComplete={hasAppeared ? undefined : onAppeared}
                    >
                        <div ref={setComposerNode} className="absolute inset-x-0 bottom-0">
                            <CollapsedComposer onOpenChange={setIsComposerOpen} />
                        </div>
                    </motion.div>
                )}
            </div>
        </div>
    );
});

export const CollapsedComposer: FunctionComponent<{
    readonly onOpenChange: (open: boolean) => void;
}> = memo(({ onOpenChange }) => {
    const { handleDraftChange, handleKeyDown, open, submit } = useChatComposerCallbacks();
    const { collapsedComposerExpandSignal } = useChatComposerActive();
    const { draft, transcription } = useChatComposerInput();
    const conversation = useChatComposerConversation();
    const { activeChatContext } = useChatComposerActive();

    return (
        <PillChatComposer
            draft={draft}
            idleSlot={<ChatIdleSlotOutlet />}
            isExpanded={false}
            isStreaming={conversation.turn.status === 'streaming'}
            openRequestSignal={collapsedComposerExpandSignal}
            topSlot={activeChatContext !== null ? <ChatPromptOutlet /> : null}
            transcription={transcription}
            onDraftChange={handleDraftChange}
            onKeyDown={handleKeyDown}
            onOpen={open}
            onOpenChange={onOpenChange}
            onSubmit={submit}
        />
    );
});
